import { useTranslation } from 'react-i18next';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import DialogWrapper from '../components/Dialog/DialogWrapper';
import { useGetCustomReportsByMetricQuery } from '../admin/requests/queries';

interface UpdateMetricConfirmationDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  metricName: string;
  metricId: string;
  isSaving: boolean;
}

function UpdateMetricConfirmationDialog({
  open,
  onCancel,
  onConfirm,
  metricName,
  metricId,
  isSaving = false,
}: UpdateMetricConfirmationDialogProps) {
  const { t } = useTranslation();

  // Fetch custom reports for the metric (only when dialog is open)
  const { data: reports = [], isLoading } = useGetCustomReportsByMetricQuery(open && !!metricId, metricId);

  return (
    <DialogWrapper
      title={t('navItems.customMetricsPage.updateConfirmationTitle', { metricName })}
      dialogOptions={{
        open,
        onClose: isSaving ? undefined : onCancel,
        maxWidth: 'sm',
        fullWidth: true,
      }}
      dataTestId='update-metric-confirmation-dialog'
    >
      <DialogContent>
        <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
          {t('navItems.customMetricsPage.updateConfirmationDescription')}
        </Typography>

        {isLoading ? (
          <Box display='flex' justifyContent='center' alignItems='center' minHeight={100}>
            <CircularProgress size={30} data-testid='reports-loading-spinner' />
          </Box>
        ) : (
          <List
            dense
            sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
          >
            {reports.map((report) => (
              <ListItem key={report.id} data-testid={`report-item-${report.id}`}>
                <ListItemText
                  primary={`• ${report.title}`}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.primary',
                  }}
                />
              </ListItem>
            ))}
            {reports.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={t('navItems.customMetricsPage.noReportsFound')}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.secondary',
                    fontStyle: 'italic',
                  }}
                />
              </ListItem>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={isSaving} data-testid='update-cancel-button'>
          {t('buttonText.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant='contained'
          disabled={isSaving || isLoading}
          data-testid='update-confirm-button'
        >
          {isSaving ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t('navItems.customMetricsPage.saving')}
            </>
          ) : (
            t('admin.centralReportsPage.managePublishReport.save')
          )}
        </Button>
      </DialogActions>
    </DialogWrapper>
  );
}

export default UpdateMetricConfirmationDialog;
